// Utility functions specifically for budget calculations and formatting

// Format percentage values
export const formatPercentage = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value}%`;
};

// Calculate remaining payment for a scope
export const getRemaining = (scope) => {
  if (!scope.budget) return null;
  
  const totalPayments = scope.payments.reduce((sum, payment) => sum + payment.amount, 0);
  return scope.budget - totalPayments;
};

// Calculate total contracts for a scope
export const getTotalContracted = (scope) => {
  return scope.contracts.reduce((sum, contract) => sum + (contract.amount || 0), 0);
};

// Calculate total payments for a scope
export const getTotalPayments = (scope) => {
  return scope.payments.reduce((sum, payment) => sum + payment.amount, 0);
};

// Determine budget warning flags for a scope
export const getBudgetWarnings = (scope) => {
  const totalContracted = getTotalContracted(scope);
  const totalPaid = getTotalPayments(scope);
  const remaining = getRemaining(scope);
  
  return {
    isOutOfBudget: remaining !== null && remaining < 0,
    isContractAmountHigherThanBudget: scope.budget && totalContracted > scope.budget,
    isPaymentsMoreThanContracted: totalContracted > 0 && totalPaid > totalContracted,
    hasNoContracts: scope.contracts.length === 0 && scope.budget && scope.budget > 0
  };
};

// Check if a scope has any budget warnings
export const hasBudgetWarnings = (scope) => {
  const warnings = getBudgetWarnings(scope);
  return warnings.isOutOfBudget || 
         warnings.isContractAmountHigherThanBudget || 
         warnings.isPaymentsMoreThanContracted || 
         warnings.hasNoContracts;
};

// Group scopes by month based on their timeline_start value
export const groupScopesByMonth = (scopes, showMonthHeaders) => {
  if (!showMonthHeaders) {
    return [{ month: null, scopes: scopes }];
  }
  
  // Create month groups
  const months = {};
  
  // Project start date is April 2025 based on the construction data
  const projectStartYear = 2025;
  const projectStartMonth = 3; // 0-based, so 3 is April
  
  // Generate month names for 24 months starting from project start date
  const monthOptions = Array.from({ length: 24 }, (_, i) => {
    const date = new Date(projectStartYear, projectStartMonth + i, 1);
    return {
      name: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      index: i
    };
  });
  
  // Initialize default group for items with no timeline
  months['unspecified'] = {
    month: 'No Start Month',
    monthIndex: -1,
    scopes: []
  };
  
  // Group scopes by their timeline_start month
  scopes.forEach(scope => {
    if (scope.start_month !== undefined && scope.start_month !== null) {
      // Use start_month directly if available
      const monthIndex = scope.start_month;
      if (!months[monthIndex]) {
        months[monthIndex] = {
          month: monthOptions.find(m => m.index === monthIndex)?.name || `Month ${monthIndex}`,
          monthIndex: monthIndex,
          scopes: []
        };
      }
      months[monthIndex].scopes.push(scope);
    } else if (scope.timeline_start !== undefined && scope.timeline_start !== null) {
      // Otherwise use timeline_start
      const monthIndex = scope.timeline_start;
      if (!months[monthIndex]) {
        months[monthIndex] = {
          month: monthOptions.find(m => m.index === monthIndex)?.name || `Month ${monthIndex}`,
          monthIndex: monthIndex,
          scopes: []
        };
      }
      months[monthIndex].scopes.push(scope);
    } else {
      // For items with no timeline data, add to unspecified group
      months['unspecified'].scopes.push(scope);
    }
  });
  
  // Convert to array and sort by monthIndex
  const result = Object.values(months)
    .filter(group => group.scopes.length > 0) // Only keep non-empty months
    .sort((a, b) => a.monthIndex - b.monthIndex);
  
  return result;
};